import React, { useState, useRef, useEffect } from "react";
import { Panel, Label, Tabset } from "lib/components";
import { CarrierSelection } from "./CarrierSelection";
import { QuoteDetails } from "./QuoteDetails";
import { HandlingReqsTable } from "../HandlingReqsTable";
import { QuoteOverview } from "./QuoteOverview";
import { AuthType } from "lib/util/AuthType";
import { DataModes, getDataFromContext, submitModel } from "lib/util/ModelUtil";
import { getTheme } from "lib/util/Theme";
import { navigateTo } from "lib/util/Navigation";
import { callApi } from "lib/util/api";
import { getLogger } from "lib/util/Logger";
import { getSelectedRate } from "../QuoteSummaryPanel";
import { showWarningSnackbar } from "lib/components/Snackbar";
import { FreightItemsTable } from "../FreightItemsTable";
import { CustomerType, PanelMode, PageType } from "../ControlConstants";
import { validateCustomerCredit } from "../OrderValidation";
import { showOrderCreationErrorDialog } from "lib/components/Dialog";

const log = getLogger("portal.customer.brokerage-ltl-quote.BrokerageLtlQuote");
const parentType = "ltl-quote";

export function BrokerageLtlQuote(props) {
    const startStep = props.startStep != null ? props.startStep : 0;
    const [currentStep, setCurrentStep] = useState(startStep);
    const [prevStep, setPrevStep] = useState();
    const [agree, setAgree] = useState(false);
    const initialState = props.initialData != null ? DataModes.EDIT : DataModes.ADD;
    const [mode, setMode] = useState(initialState);
    const image = { image: "LtlPalletQuote", height: 40, width: 200, marginRight: 10, fill: getTheme().palette.primary.base};
    const setStep = (step) => {
      setCurrentStep(step);
      setMode(DataModes.EDIT);
    }
    const [dataList, setDataList] = useState();
    const firstUpdate = useRef(true);

    useEffect(() => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        if(props.initialData)
          setDataList(props.initialData);
        return;
      }
    });

    useEffect(() => {
      if(currentStep !== 1)
        validateCustomerCredit(parentType);
    }, [currentStep]);

    const freightItemsTableOverview = FreightItemsTable(CustomerType.BROKERAGE, PanelMode.EDIT, PageType.OVERVIEW, prevStep);
    const freightItemsTableDetail = FreightItemsTable(CustomerType.BROKERAGE, PanelMode.EDIT, PageType.DETAILS, prevStep);
    const step1FreightItemsTable = currentStep == 0 ? freightItemsTableOverview : null;
    const step3FreightItemsTable = currentStep == 2 ? freightItemsTableDetail : null;

    const handlingReqsTable = HandlingReqsTable(currentStep, prevStep);
    const step1HandlingReqsTable = currentStep == 0 ? handlingReqsTable : null;
    const step3HandlingReqsTable = currentStep == 2 ? handlingReqsTable : null;

    return (
        <Panel  modelName="dispatch/ltl-quote-order" dataMode={mode} loadInitialData={(context) => loadInitialData(context, props.initialData)}
            fillHeight fillRow>
            <Label image={image} look="primary6" marginBottom={10} fillRow />
            <Tabset variant="wizard" keepMounted selectedIndex={currentStep} {...props}
              setCurrentStep={setStep} setPrevStep={setPrevStep} onSubmit={(event, context, setLoading) => convertQuoteToOrder(event, context, setLoading, agree)}
              fillRow fillHeight >
                {QuoteOverview(step1FreightItemsTable, step1HandlingReqsTable, currentStep)}
                {CarrierSelection()}
                {QuoteDetails(props, setAgree, step3FreightItemsTable, step3HandlingReqsTable, currentStep)}
            </Tabset>
        </Panel>
    )
}

function convertQuoteToOrder(event, context, setLoading, agree) {
  if (!timeValidation(context) || !context.validateForm() || !validateCustomerCredit(parentType)) {
    return;
  }
  //const agreed = getDataFromContext(context, "agreed_to_terms"); //when coming from quote list, this isn't in context. Could add to model
  if (agree !== true) {
    showWarningSnackbar("Please accept the Terms and Conditions before proceeding.", true);
  }
  else {
  	setLoading(true);
    const data = getDataFromContext(context);
    const selectedRate = getSelectedRate(data);
    submitModel(context, null, (submitResponse) => {
      callApi("api/portal/customer/convert-quote-to-order", "POST", {quote_id: data.id, rate_selection_id: selectedRate.bfg_uid}).then((convertResponse) => {
        setLoading(false);
        log.debug("Convert response %o", convertResponse);
        navigateTo("Confirmation", null, false, {data: convertResponse.data[0].order});
      }).catch(reason => {
        setLoading(false);
        showOrderCreationErrorDialog("Error", reason);
      });
    }, (reason) => showOrderCreationErrorDialog("Error", reason), (data) => adjustDataBeforePosting(data), DataModes.EDIT);
  }
}

function adjustDataBeforePosting(data)
{
  delete data[0].disclaimer;
  delete data[0].shipper;
  delete data[0].ship_date;
  delete data[0].consignee;
  return data;
}

function timeValidation(context) {
	const stops = getDataFromContext(context, "stops");
	const early = stops[0]["sched_arrive_early"];
	const late = stops[0]["sched_arrive_late"];
	if(early && late ){
		let earlyDate, lateDate;
		if(typeof early === "object" && early.value)
			earlyDate = new Date(early.value);
		else
			earlyDate = new Date(early);
		if(typeof late === "object" && late.value)
			lateDate = new Date(late.value);
		else
			lateDate = new Date(late);
		if (earlyDate.getTime() > lateDate.getTime()){
			showWarningSnackbar("Early pickup time is later than late pickup time.", true);
			return false;
		}
	}
	return true;
}

function loadInitialData(context, initialData){
  const updatedDataList = [...context.data.list];
  if(initialData != null){
      for (let i = 0; i < initialData.length; i++)
        updatedDataList.push({modelData:initialData[i]});
      context.data.setDataList(updatedDataList);
  }
}

export function getPageOptions() {
    return {
      auth: AuthType.CUSTOMER,
      title: "Add Quote",
    };
}
